/** Display only. Callers supply already currency-normalized, unscaled values.
 * L1–L3 (including record lists) use millions throughout; hourly rates use thousands
 * and Deal view uses base currency. Never sort, calculate or convert FX with labels. */
export type DisplayCurrency = string | { code: string | null } | null | undefined;

export const moneyCurrencyCode = (currency: DisplayCurrency) =>
  (typeof currency === "string" ? currency : currency?.code) || "SEK";
export const millionMoneyUnit = (currency: DisplayCurrency) => `M${moneyCurrencyCode(currency)}`;

// Reuses Pipeline Health's existing precision policy, including small non-zero
// amounts and the explicit comparison precision requested by What changed.
export function formatMillions(value: number, minimumDecimals = 1): string {
  if (!Number.isFinite(value)) return "—";
  const millions = value / 1_000_000;
  const decimals = Math.max(minimumDecimals, millions !== 0 && Math.abs(millions) < 0.05
    ? Math.min(100, 1 - Math.floor(Math.log10(Math.abs(millions)))) : 1);
  let number = millions.toFixed(decimals);
  while (number.endsWith("0") && number.length - number.indexOf(".") - 1 > minimumDecimals) number = number.slice(0, -1);
  return number;
}

export function formatAnalysisMoney(value: number, currency: DisplayCurrency, minimumDecimals = 1): string {
  const number = formatMillions(value, minimumDecimals);
  return number === "—" ? number : `${number} ${millionMoneyUnit(currency)}`;
}

export function formatBaseMoney(value: number, currency: DisplayCurrency): string {
  if (!Number.isFinite(value)) return "—";
  const decimals = value !== 0 && Math.abs(value) < .01
    ? Math.min(20, 1 - Math.floor(Math.log10(Math.abs(value)))) : 2;
  const number = new Intl.NumberFormat("en-GB", { maximumFractionDigits: decimals }).format(Object.is(value, -0) ? 0 : value);
  return `${number} ${moneyCurrencyCode(currency)}`;
}

export function formatRevenuePerHour(value: number, currency: DisplayCurrency): string {
  if (!Number.isFinite(value)) return "—";
  const unit = `k${moneyCurrencyCode(currency)}/h`;
  // Keep a common scale and avoid turning a small non-zero rate into a zero.
  if (value !== 0 && Math.abs(value) < 50) return `${value > 0 ? "<0.1" : ">-0.1"} ${unit}`;
  const number = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(value === 0 ? 0 : value / 1_000);
  return `${number} ${unit}`;
}

/** Presentation guard only; existing factual/analytical validation stays intact.
 * A writer may not reintroduce k-units or base currency into an M-unit readout. */
export function usesAnalysisMoneyUnits(text: string, currency: DisplayCurrency): boolean {
  const code = moneyCurrencyCode(currency).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const units = text.match(new RegExp(`\\b[km]?${code}\\b`, "gi")) ?? [];
  return units.every(unit => unit === millionMoneyUnit(currency));
}
